import { apiGet, apiPatch } from './client';
import type {
  IdxComposition,
  IdxCompositionBatchRequest,
  IdxCompositionKey,
} from '../types/IdxComposition';

// Server returns rows without `idKey` — the page needs a single key for the
// grid. Synthesize `idxId|assetId` here so callers (LopCrudPage idField) work
// uniformly. Same recipe as calcMethodMap.ts.
function withIdKey(rows: Omit<IdxComposition, 'idKey'>[]): IdxComposition[] {
  return rows.map((r) => ({ ...r, idKey: `${r.idxId}|${r.assetId}` }));
}

function stripIdKey(rows: IdxComposition[]): Omit<IdxComposition, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): IdxCompositionKey {
  const [i, a] = idKey.split('|');
  return { idxId: Number(i), assetId: Number(a) };
}

function buildQuery(idxId?: number): string {
  const params = new URLSearchParams();
  if (idxId != null) params.set('idxId', String(idxId));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getIdxCompositions(idxId?: number): Promise<IdxComposition[]> {
  const rows = await apiGet<Omit<IdxComposition, 'idKey'>[]>(
    `/api/idx-compositions${buildQuery(idxId)}`,
  );
  return withIdKey(rows);
}

export async function saveIdxCompositionsBatch(
  added: IdxComposition[],
  modified: IdxComposition[],
  deletedIdKeys: string[],
  idxId?: number,
): Promise<IdxComposition[]> {
  const body: IdxCompositionBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
    idxId,
  };
  const fresh = await apiPatch<
    IdxCompositionBatchRequest,
    Omit<IdxComposition, 'idKey'>[]
  >('/api/idx-compositions/batch', body);
  return withIdKey(fresh);
}
